import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Store, LayoutGrid, Users, LogOut, Plus, Pencil, Trash2, GripVertical, ChevronDown, ChevronRight, Utensils, Eye, ImagePlus, X, UserCheck, Settings, Info, FileText, ChevronLeft, Package, Search, Download, Mail } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";

const fmtPrice = (p) => `$${Number(p).toLocaleString("es-CO")}`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (path) => path ? `${BACKEND_URL}/api/files/${path}` : null;

function ItemVisual({ icon, imagePath, size = "text-xl", imgClass = "w-8 h-8" }) {
  if (imagePath) return <img src={imgUrl(imagePath)} alt="" className={`${imgClass} rounded-md object-cover`} />;
  return <span className={size}>{icon || "📝"}</span>;
}

const EMOJI_LIST = [
  { cat: "Comida", emojis: ["🥟", "🍲", "🥗", "🥩", "🍔", "🌭", "🍗", "🍖", "🥓", "🫕", "🥘", "🍛", "🍝", "🌮", "🌯", "🥪", "🧆", "🥚", "🫔", "🥐"] },
  { cat: "Bebidas", emojis: ["🍹", "🍺", "🍷", "🥂", "🍸", "🧃", "🥛", "☕", "🍵", "🧊", "🍋", "🫗", "🍾", "🥃", "🧋"] },
  { cat: "Postres", emojis: ["🍰", "🎂", "🍫", "🍪", "🍩", "🍨", "🍧", "🧁", "🍮", "🍌", "🍓", "🍒", "🍑", "🥭", "🍍"] },
  { cat: "Otros", emojis: ["📦", "🔥", "⭐", "🌅", "🍽️", "🧀", "🫓", "🥜", "🐔", "🐟", "🦐", "🍅", "🌽", "🫒", "🧈"] },
];

function InlineEmojiPicker({ onSelect }) {
  return (
    <div style={{
      background: "white", borderRadius: "12px", padding: "12px", marginTop: "8px",
      border: "1px solid #E6E4DD", boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      maxHeight: "200px", overflowY: "auto"
    }}>
      {EMOJI_LIST.map(group => (
        <div key={group.cat} style={{ marginBottom: "8px" }}>
          <p style={{ fontSize: "0.65rem", fontWeight: 700, color: "#6B6A65", textTransform: "uppercase", marginBottom: "4px" }}>{group.cat}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
            {group.emojis.map(em => (
              <button key={em} type="button" onClick={() => onSelect(em)}
                style={{ fontSize: "1.3rem", padding: "4px", borderRadius: "6px", border: "none", cursor: "pointer", background: "transparent", lineHeight: 1 }}
                onMouseEnter={ev => ev.target.style.background = "#F5EFE0"}
                onMouseLeave={ev => ev.target.style.background = "transparent"}>{em}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const fmtCode = (c) => { const s = String(c || ""); return s.endsWith(".0") ? s.slice(0, -2) : s; };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("restaurants");
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [stdMenus, setStdMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [showRestDialog, setShowRestDialog] = useState(false);
  const [editRest, setEditRest] = useState(null);
  const [restForm, setRestForm] = useState({ name: "", slug: "", owner_email: "", description: "", city: "", main_menu_id: "", happy_hour_menu_id: "", template_slug: "classic", primary_color: "#3E1F0D", accent_color: "#D4772C", bg_color: "#F5EFE0", font_family: "Manrope", logo_url: "", gtm_container_id: "", seo_index: false, happy_hour_schedule: { days: [], start_time: "", end_time: "" } });
  const [restFilterName, setRestFilterName] = useState("");
  const [restFilterType, setRestFilterType] = useState("");
  const [restPage, setRestPage] = useState(1);
  const [restPerPage, setRestPerPage] = useState(10);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [accessRestIds, setAccessRestIds] = useState([]);
  const [showRestImportDialog, setShowRestImportDialog] = useState(false);
  const [restImportPreview, setRestImportPreview] = useState(null);
  const [restImportApplying, setRestImportApplying] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", icon: "", description: "", subtitle: "", image_url: "", image_path: "" });
  const [editCat, setEditCat] = useState(null);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [itemForm, setItemForm] = useState({ category_id: "", name: "", description: "", price: 0, icon: "", allergens: "", badges: "", image_path: "" });
  const [editItem, setEditItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [menuForm, setMenuForm] = useState({ name: "", description: "" });
  const [editMenu, setEditMenu] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // "cat" | "item" | false
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", name: "", role: "restaurant" });
  const [products, setProducts] = useState([]);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: "", description: "", product_units: "", base_price: 0, unique_code: "", icon: "", image_path: "", image_url: "", allergens: "", badges: "" });
  const [productSearch, setProductSearch] = useState("");
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignCatId, setAssignCatId] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [productMenuPrices, setProductMenuPrices] = useState([]);
  const [showBulkProductsDialog, setShowBulkProductsDialog] = useState(false);
  const [bulkProductsPreview, setBulkProductsPreview] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [showMenuImportDialog, setShowMenuImportDialog] = useState(false);
  const [menuImportPreview, setMenuImportPreview] = useState(null);
  const [menuImportErrors, setMenuImportErrors] = useState([]);
  const [menuImportApplying, setMenuImportApplying] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [s, r, sm, p] = await Promise.all([
        api.admin.stats(), api.admin.getRestaurants(), api.admin.getStandardMenus(), api.admin.getProducts()
      ]);
      setStats(s.data);
      setRestaurants(r.data);
      setStdMenus(sm.data);
      setProducts(p.data);
      // Load first menu content if available
      if (sm.data.length > 0 && !selectedMenuId) {
        setSelectedMenuId(sm.data[0].id);
      }
    } catch (e) { console.error(e); }
  }, [selectedMenuId]);

  // Load menu content when selectedMenuId changes
  const loadMenuContent = useCallback(async (mid) => {
    if (!mid) { setCategories([]); setItems([]); return; }
    try {
      const { data } = await api.admin.getMenuContent(mid);
      setCategories(data.categories);
      setItems(data.items);
    } catch (e) { setCategories([]); setItems([]); }
  }, []);

  useEffect(() => { if (selectedMenuId) loadMenuContent(selectedMenuId); }, [selectedMenuId, loadMenuContent]);

  useEffect(() => {
    api.auth.me().then((r) => { setUser(r.data); if (r.data.role !== "admin") navigate("/login"); })
      .catch(() => navigate("/login"));
    loadData();
  }, [navigate, loadData]);

  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); navigate("/login"); };

  const impersonateUser = async (u) => {
    try {
      const { data } = await api.admin.impersonate(u.id);
      localStorage.setItem("admin_token", localStorage.getItem("token"));
      localStorage.setItem("admin_user", localStorage.getItem("user"));
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      if (data.user.role === "admin") navigate("/admin");
      else navigate("/restaurant");
    } catch (e) { toast.error("Error al ingresar como usuario"); }
  };

  // Restaurant CRUD
  const openNewRest = () => { setEditRest(null); setRestForm({ name: "", slug: "", owner_email: "", description: "", city: "", main_menu_id: "", happy_hour_menu_id: "", template_slug: "classic", primary_color: "#3E1F0D", accent_color: "#D4772C", bg_color: "#F5EFE0", font_family: "Manrope", logo_url: "", gtm_container_id: "", seo_index: false, happy_hour_schedule: { days: [], start_time: "", end_time: "" } }); setShowRestDialog(true); };
  const openEditRest = (r) => { setEditRest(r); setRestForm({ name: r.name, slug: r.slug, owner_email: r.owner_email, description: r.description || "", city: r.city || "", main_menu_id: r.main_menu_id || "", happy_hour_menu_id: r.happy_hour_menu_id || "", template_slug: r.template_slug || "classic", primary_color: r.primary_color || "#3E1F0D", accent_color: r.accent_color || "#D4772C", bg_color: r.bg_color || "#F5EFE0", font_family: r.font_family || "Manrope", logo_url: r.logo_url || "", gtm_container_id: r.gtm_container_id || "", seo_index: r.seo_index || false, happy_hour_schedule: r.happy_hour_schedule || { days: [], start_time: "", end_time: "" } }); setShowRestDialog(true); };
  const saveRest = async () => {
    try {
      if (editRest) {
        // Check if menu assignments changed and there are overrides
        const mainChanged = restForm.main_menu_id !== (editRest.main_menu_id || "");
        const hhChanged = restForm.happy_hour_menu_id !== (editRest.happy_hour_menu_id || "");
        if (mainChanged || hhChanged) {
          const { data: counts } = await api.admin.getOverridesCount(editRest.id);
          const warnings = [];
          if (mainChanged && counts.main > 0) warnings.push(`Menu Principal (${counts.main} personalizaciones)`);
          if (hhChanged && counts.happy_hour > 0) warnings.push(`Happy Hour (${counts.happy_hour} personalizaciones)`);
          if (warnings.length > 0) {
            const confirmed = window.confirm(
              `Este restaurante tiene cambios personalizados en:\n\n${warnings.join("\n")}\n\nCambiar el menu asignado eliminara todos los precios y secciones modificados para ese tipo de menu.\n\nDeseas continuar?`
            );
            if (!confirmed) return;
            if (mainChanged && counts.main > 0) await api.admin.deleteOverrides(editRest.id, "main");
            if (hhChanged && counts.happy_hour > 0) await api.admin.deleteOverrides(editRest.id, "happy_hour");
          }
        }
        await api.admin.updateRestaurant(editRest.id, restForm);
      } else {
        await api.admin.createRestaurant(restForm);
      }
      toast.success(editRest ? "Restaurante actualizado" : "Restaurante creado");
      setShowRestDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const deleteRest = async (id) => {
    if (!window.confirm("Eliminar restaurante?")) return;
    await api.admin.deleteRestaurant(id);
    toast.success("Eliminado");
    loadData();
  };

  // Category CRUD
  const openNewCat = () => { setEditCat(null); setCatForm({ name: "", icon: "", description: "", subtitle: "", image_url: "", image_path: "" }); setShowCatDialog(true); };
  const openEditCat = (c) => { setEditCat(c); setCatForm({ name: c.name, icon: c.icon, description: c.description || "", subtitle: c.subtitle || "", image_url: c.image_url || "", image_path: c.image_path || "" }); setShowCatDialog(true); };
  const saveCat = async () => {
    try {
      if (editCat) await api.admin.updateCategory(selectedMenuId, editCat.id, catForm);
      else await api.admin.createCategory(selectedMenuId, catForm);
      toast.success(editCat ? "Categoría actualizada" : "Categoría creada");
      setShowCatDialog(false);
      loadMenuContent(selectedMenuId);
    } catch (e) { toast.error("Error"); }
  };
  const deleteCat = async (id) => {
    if (!window.confirm("Eliminar categoria y todos sus items?")) return;
    await api.admin.deleteCategory(selectedMenuId, id);
    toast.success("Eliminado");
    loadMenuContent(selectedMenuId);
  };

  // Item CRUD
  const openNewItem = (catId) => {
    setAssignCatId(catId);
    setAssignSearch("");
    setShowAssignDialog(true);
  };
  const openEditItem = (item) => {
    setEditItem(item);
    setItemForm({ category_id: item.category_id, name: item.name, description: item.description || "",
      price: item.price, icon: item.icon || "", allergens: (item.allergens || []).join(", "), badges: (item.badges || []).join(", "), image_path: item.image_path || "" });
    setShowItemDialog(true);
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.upload(fd);
      setItemForm(p => ({ ...p, image_path: data.path }));
      toast.success("Imagen subida");
    } catch (err) { toast.error("Error al subir imagen"); }
    finally { setUploadingImage(false); }
  };
  const saveItem = async () => {
    const payload = { ...itemForm, price: Number(itemForm.price),
      allergens: itemForm.allergens ? itemForm.allergens.split(",").map(s => s.trim()).filter(Boolean) : [],
      badges: itemForm.badges ? itemForm.badges.split(",").map(s => s.trim()).filter(Boolean) : [] };
    try {
      if (editItem) await api.admin.updateItem(selectedMenuId, editItem.id, payload);
      else await api.admin.createItem(selectedMenuId, payload);
      toast.success(editItem ? "Item actualizado" : "Item creado");
      setShowItemDialog(false);
      loadMenuContent(selectedMenuId);
    } catch (e) { toast.error("Error"); }
  };
  const deleteItem = async (id) => {
    if (!window.confirm("Quitar producto del menú?")) return;
    await api.admin.removeMenuProduct(selectedMenuId, id);
    toast.success("Producto removido del menú");
    loadMenuContent(selectedMenuId);
  };

  // Drag & Drop
  const handleDragEnd = async (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (type === "CATEGORY") {
      const newCats = Array.from(categories);
      const [moved] = newCats.splice(source.index, 1);
      newCats.splice(destination.index, 0, moved);
      const reordered = newCats.map((c, i) => ({ ...c, order: i }));
      setCategories(reordered);
      await api.admin.reorderMenu(selectedMenuId, { categories: reordered.map(c => ({ id: c.id, order: c.order })) });
    } else if (type === "ITEM" && source.droppableId === destination.droppableId) {
      const catId = source.droppableId;
      const catItems = items.filter(i => i.category_id === catId).sort((a, b) => a.order - b.order);
      const [moved] = catItems.splice(source.index, 1);
      catItems.splice(destination.index, 0, moved);
      const reordered = catItems.map((item, i) => ({ ...item, order: i }));
      setItems(prev => [...prev.filter(i => i.category_id !== catId), ...reordered]);
      await api.admin.reorderMenu(selectedMenuId, { items: reordered.map(i => ({ id: i.id, order: i.order })) });
    }
  };

  const toggleCat = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));

  const sidebarItems = [
    { id: "restaurants", label: "Restaurantes", icon: Store },
    { id: "products", label: "Productos", icon: Package },
    { id: "menu", label: "Lista de Menús", icon: LayoutGrid },
  ];

  const configItems = [
    { id: "users", label: "Usuarios", icon: Users },
    { id: "smtp", label: "Configuración SMTP", icon: Mail },
    { id: "logs", label: "Logs de Precios", icon: FileText },
  ];

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsLimit, setLogsLimit] = useState(10);
  const [logsDateFrom, setLogsDateFrom] = useState("");
  const [logsDateTo, setLogsDateTo] = useState("");
  // Users state
  const [usersSearch, setUsersSearch] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersRefresh, setUsersRefresh] = useState(0);
  // SMTP state
  const [smtpForm, setSmtpForm] = useState({ host: "", port: 587, username: "", password: "", security: "TLS", sender_email: "", sender_name: "MenuManager", enabled: false });
  const [smtpTesting, setSmtpTesting] = useState(false);

  const loadLogs = () => {
    const params = { page: logsPage, limit: logsLimit };
    if (logsDateFrom) params.date_from = logsDateFrom;
    if (logsDateTo) params.date_to = logsDateTo;
    api.admin.getPriceLogs(params).then(r => { setLogs(r.data.logs); setLogsTotal(r.data.total); setLogsTotalPages(r.data.pages); }).catch(() => {});
  };
  useEffect(() => {
    if (tab === "users") {
      api.admin.getUsers({ search: usersSearch, page: usersPage, limit: 10 }).then(r => { setUsers(r.data.users); setUsersTotal(r.data.total); setUsersTotalPages(r.data.pages); }).catch(() => {});
    }
  }, [tab, usersPage, usersSearch, usersRefresh]);
  const refreshUsers = () => setUsersRefresh(c => c + 1);
  useEffect(() => { if (tab === "smtp") { api.admin.getSmtp().then(r => setSmtpForm(r.data)).catch(() => {}); } }, [tab]);

  if (!user) return null;

  return (
    <div className="dashboard-layout" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="flex items-center gap-3 mb-8 px-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#C05C46' }}>
            <Utensils className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm" style={{ color: '#2A2A2A' }}>MenuManager</div>
            <div className="text-xs" style={{ color: '#6B6A65' }}>Admin</div>
          </div>
        </div>
        <nav className="flex-1">
          {sidebarItems.map(s => (
            <div key={s.id} className={`sidebar-nav-item ${tab === s.id ? "active" : ""}`}
              onClick={() => setTab(s.id)} data-testid={`nav-${s.id}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </div>
          ))}
        </nav>
        {/* Config section at bottom */}
        <div>
          <div className="border-t pt-3 mb-1" style={{ borderColor: '#E6E4DD' }}>
            <div className="flex items-center gap-2 px-3 mb-2">
              <Settings className="h-3.5 w-3.5" style={{ color: '#A89F9A' }} />
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#A89F9A' }}>Configuracion</span>
            </div>
            {configItems.map(s => (
              <div key={s.id} className={`sidebar-nav-item ${tab === s.id ? "active" : ""}`}
                onClick={() => setTab(s.id)} data-testid={`nav-${s.id}`}>
                <s.icon className="h-4 w-4" /> {s.label}
              </div>
            ))}
          </div>
          <div className="border-t pt-4 mt-2" style={{ borderColor: '#E6E4DD' }}>
            <div className="text-xs mb-2 px-2 truncate" style={{ color: '#6B6A65' }}>{user?.email}</div>
            <div className="sidebar-nav-item" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Cerrar Sesion
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="dashboard-main">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fade-in">
            {[{ label: "Restaurantes", value: stats.restaurants }, { label: "Usuarios", value: stats.users },
              { label: "Productos", value: products.length }, { label: "Menús", value: stdMenus.length }].map(s => (
              <div key={s.label} className="stat-card">
                <div className="text-xs font-body uppercase tracking-wide" style={{ color: '#6B6A65' }}>{s.label}</div>
                <div className="text-3xl font-heading font-bold mt-1" style={{ color: '#2A2A2A' }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Restaurants Tab */}
        {tab === "restaurants" && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Restaurantes</h2>
              <div className="flex gap-2">
                <button onClick={() => {
                  const header = "name\tdescription\tcity\towner_email\tslug\n";
                  const ex = restaurants.slice(0, 2).map(r => `${r.name}\t${r.description || ""}\t${r.city || ""}\t${r.owner_email}\t${r.slug}`).join("\n");
                  const blob = new Blob([header + (ex || "Mi Restaurante\tDescripción\tBogotá\tchef@email.com\tmi-restaurante")], { type: "text/tab-separated-values" });
                  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "template_restaurantes.xls"; a.click();
                }} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hover:bg-gray-50 text-xs font-medium" style={{ borderColor: '#E6E4DD' }}>
                  <Download className="h-3 w-3" style={{ color: '#6B6A65' }} /> Template
                </button>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer hover:bg-gray-50 text-xs font-medium" style={{ borderColor: '#E6E4DD' }}>
                  <ImagePlus className="h-3 w-3" style={{ color: '#C05C46' }} /> Importar
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    try {
                      const { data } = await api.admin.restaurantImportPreview(fd);
                      setRestImportPreview(data); setShowRestImportDialog(true);
                    } catch (err) { toast.error("Error al procesar archivo"); }
                    e.target.value = "";
                  }} />
                </label>
                <Button onClick={openNewRest} style={{ background: '#C05C46' }} data-testid="add-restaurant-btn">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo Restaurante
                </Button>
              </div>
            </div>
            {/* Filters */}
            <div className="flex gap-3 mb-4 flex-wrap items-end">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4" style={{ color: '#A89F9A' }} />
                <input type="text" placeholder="Buscar por nombre..." value={restFilterName} onChange={e => { setRestFilterName(e.target.value); setRestPage(1); }}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm" style={{ borderColor: '#E6E4DD' }} />
              </div>
              <select value={restFilterType} onChange={e => { setRestFilterType(e.target.value); setRestPage(1); }}
                className="text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD', minWidth: '150px' }}>
                <option value="">Todos los tipos</option>
                {[...new Set(restaurants.map(r => r.description).filter(Boolean))].sort().map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select value={restPerPage} onChange={e => { setRestPerPage(Number(e.target.value)); setRestPage(1); }}
                className="text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}>
                <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
              </select>
            </div>
            {(() => {
              const filtered = restaurants.filter(r => {
                if (restFilterName && !r.name.toLowerCase().includes(restFilterName.toLowerCase())) return false;
                if (restFilterType && r.description !== restFilterType) return false;
                return true;
              });
              const totalPages = Math.ceil(filtered.length / restPerPage) || 1;
              const paged = filtered.slice((restPage - 1) * restPerPage, restPage * restPerPage);
              return (
                <>
                  <div className="bg-white border rounded-lg overflow-hidden mb-4" style={{ borderColor: '#E6E4DD' }}>
                    <table className="w-full">
                      <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                        <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Restaurante</th>
                        <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Ciudad</th>
                        <th className="text-center p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Menús</th>
                        <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Acciones</th>
                      </tr></thead>
                      <tbody>
                        {paged.map(r => (
                          <tr key={r.id} className="border-b last:border-0" style={{ borderColor: '#E6E4DD' }}>
                            <td className="p-3">
                              <div className="font-medium text-sm" style={{ color: '#2A2A2A' }}>{r.name}{r.city ? ` - ${r.city}` : ""}</div>
                              {r.description && <div className="text-[10px]" style={{ color: '#A89F9A' }}>{r.description}</div>}
                              <div className="text-xs" style={{ color: '#6B6A65' }}>/{r.slug} · {r.owner_email}</div>
                            </td>
                            <td className="p-3 text-xs" style={{ color: '#6B6A65' }}>{r.city || "-"}</td>
                            <td className="p-3 text-center">
                              <div className="flex gap-1 justify-center">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.main_menu_id ? "bg-green-100 text-green-700" : "bg-red-100 text-red-500"}`}>
                                  {r.main_menu_id ? "✓" : "✗"} Principal
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.happy_hour_menu_id ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                                  {r.happy_hour_menu_id ? "✓" : "—"} HH
                                </span>
                              </div>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex gap-1 justify-end">
                                <a href={`/menu/${r.slug}`} target="_blank" rel="noreferrer">
                                  <Button size="sm" variant="ghost"><Eye className="h-3 w-3" /></Button>
                                </a>
                                <Button size="sm" variant="ghost" onClick={() => openEditRest(r)}><Pencil className="h-3 w-3" /></Button>
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteRest(r.id)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paged.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-sm" style={{ color: '#6B6A65' }}>No hay restaurantes</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: '#6B6A65' }}>{filtered.length} restaurantes</span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" disabled={restPage <= 1} onClick={() => setRestPage(p => p - 1)}><ChevronLeft className="h-3 w-3" /></Button>
                      <span className="text-xs" style={{ color: '#6B6A65' }}>Página {restPage} de {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={restPage >= totalPages} onClick={() => setRestPage(p => p + 1)}><ChevronRight className="h-3 w-3" /></Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Products Tab */}
        {tab === "products" && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Productos Globales</h2>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => {
                  const header = "unique_code\tname\tdescription\tproduct_units\tbase_price\ticon\timage_url\tallergens\tbadges\n";
                  const rows = products.slice(0, 3).map(p => `${p.unique_code}\t${p.name}\t${p.description || ""}\t${p.product_units || ""}\t${p.base_price}\t${p.icon || ""}\t${p.image_url || ""}\t${(p.allergens||[]).join(", ")}\t${(p.badges||[]).join(", ")}`).join("\n");
                  const blob = new Blob([header + rows], { type: "text/tab-separated-values" });
                  const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
                  a.download = "template_productos.xls"; a.click();
                }} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border hover:bg-gray-50 text-xs font-medium" style={{ borderColor: '#E6E4DD' }}>
                  <Download className="h-3 w-3" style={{ color: '#6B6A65' }} /> Template Productos
                </button>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer hover:bg-gray-50 text-xs font-medium" style={{ borderColor: '#E6E4DD' }}>
                  <ImagePlus className="h-3 w-3" style={{ color: '#C05C46' }} /> Importar Productos
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    try {
                      const { data } = await api.admin.bulkImportProducts(fd);
                      setBulkProductsPreview(data); setShowBulkProductsDialog(true);
                    } catch (err) { toast.error("Error al procesar archivo"); }
                    e.target.value = "";
                  }} />
                </label>
                <Button onClick={() => { setEditProduct(null); setProductForm({ name: "", description: "", product_units: "", base_price: 0, unique_code: "", icon: "", image_path: "", image_url: "", allergens: "", badges: "" }); setShowProductDialog(true); }}
                  style={{ background: '#C05C46' }} data-testid="add-product-btn">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo Producto
                </Button>
              </div>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-3 h-4 w-4" style={{ color: '#A89F9A' }} />
              <input type="text" placeholder="Buscar productos..." value={productSearch} onChange={e => setProductSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E6E4DD' }} data-testid="product-search" />
            </div>
            <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: '#E6E4DD' }}>
              <table className="w-full">
                <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Código</th>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Producto</th>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Unidades</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Precio Base</th>
                  <th className="text-center p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Menús</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Acciones</th>
                </tr></thead>
                <tbody>
                  {products.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.unique_code||"").toLowerCase().includes(productSearch.toLowerCase())).map(p => (
                    <tr key={p.id} className="border-b last:border-0" style={{ borderColor: '#E6E4DD' }}>
                      <td className="p-3 text-xs font-mono" style={{ color: '#6B6A65' }}>{fmtCode(p.unique_code)}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{p.icon || "📦"}</span>
                          <div>
                            <div className="font-medium text-sm" style={{ color: '#2A2A2A' }}>{p.name}</div>
                            <div className="text-xs truncate max-w-[200px]" style={{ color: '#6B6A65' }}>{p.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-xs" style={{ color: '#6B6A65' }}>{p.product_units || "-"}</td>
                      <td className="p-3 text-sm text-right font-bold" style={{ color: '#C05C46' }}>{fmtPrice(p.base_price)}</td>
                      <td className="p-3 text-center"><span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: '#C05C4610', color: '#C05C46' }}>{p.menu_count || 0}</span></td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditProduct(p); setProductForm({ name: p.name, description: p.description || "", product_units: p.product_units || "", base_price: p.base_price, unique_code: p.unique_code || "", icon: p.icon || "", image_path: p.image_path || "", image_url: p.image_url || "", allergens: (p.allergens||[]).join(", "), badges: (p.badges||[]).join(", ") });
                            api.admin.getProductMenus(p.id).then(r => setProductMenuPrices(r.data.menu_products)).catch(() => setProductMenuPrices([]));
                            setShowProductDialog(true);
                          }}><Pencil className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                            const { data } = await api.admin.deleteProduct(p.id, false);
                            if (data.requires_force) {
                              if (window.confirm(`Producto usado en ${data.usage_count} menus. Eliminar?`)) {
                                await api.admin.deleteProduct(p.id, true); toast.success("Eliminado"); loadData();
                              }
                            } else { toast.success("Eliminado"); loadData(); }
                          }}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: '#6B6A65' }}>No hay productos. Crea tu primer producto global.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Standard Menu Tab - Two Panel Layout */}
        {tab === "menu" && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Menús Estándar</h2>
              <Button onClick={() => { setEditMenu(null); setMenuForm({ name: "", description: "" }); setShowMenuDialog(true); }}
                style={{ background: '#C05C46' }} data-testid="add-menu-btn">
                <Plus className="h-4 w-4 mr-2" /> Nuevo Menú
              </Button>
            </div>
            <div className="flex gap-4" style={{ minHeight: "500px" }}>
              {/* LEFT: Menu list */}
              <div className="w-64 flex-shrink-0 space-y-2">
                {stdMenus.map(m => (
                  <div key={m.id} onClick={() => { setSelectedMenuId(m.id); }}
                    className="p-3 rounded-lg border cursor-pointer transition-all"
                    style={{ borderColor: selectedMenuId === m.id ? '#C05C46' : '#E6E4DD', background: selectedMenuId === m.id ? '#C05C4608' : 'white' }}>
                    <div className="font-heading font-bold text-sm" style={{ color: selectedMenuId === m.id ? '#C05C46' : '#2A2A2A' }}>{m.name}</div>
                    {m.unique_code && <div className="text-[10px] font-mono" style={{ color: '#6B6A65' }}>{fmtCode(m.unique_code)}</div>}
                    <div className="flex gap-1 mt-2">
                      <button onClick={(e) => { e.stopPropagation(); setEditMenu(m); setMenuForm({ name: m.name, description: m.description || "", unique_code: m.unique_code || "" }); setShowMenuDialog(true); }}
                        className="text-[10px] px-2 py-0.5 rounded border hover:bg-gray-50" style={{ borderColor: '#E6E4DD' }}>Editar</button>
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        const { data } = await api.admin.deleteStandardMenu(m.id, false);
                        if (data.requires_force) {
                          if (window.confirm(`Menú en uso por: ${data.restaurants.map(r => r.name).join(", ")}. ¿Eliminar?`)) {
                            await api.admin.deleteStandardMenu(m.id, true);
                            toast.success("Menú eliminado"); if (selectedMenuId === m.id) { setSelectedMenuId(null); setCategories([]); setItems([]); } loadData();
                          }
                        } else { toast.success("Menú eliminado"); if (selectedMenuId === m.id) { setSelectedMenuId(null); setCategories([]); setItems([]); } loadData(); }
                      }} className="text-[10px] px-2 py-0.5 rounded border hover:bg-red-50 text-red-500" style={{ borderColor: '#E6E4DD' }}>Eliminar</button>
                      <label className="text-[10px] px-2 py-0.5 rounded border hover:bg-gray-50 cursor-pointer" style={{ borderColor: '#E6E4DD', color: '#C05C46' }}>
                        Importar
                        <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async (ev) => {
                          ev.stopPropagation(); const file = ev.target.files?.[0]; if (!file) return;
                          setSelectedMenuId(m.id);
                          const fd = new FormData(); fd.append("file", file);
                          try {
                            const { data } = await api.admin.menuImportPreview(m.id, fd);
                            setMenuImportPreview(data.preview); setMenuImportErrors(data.errors); setShowMenuImportDialog(true);
                          } catch (err) { toast.error("Error al procesar archivo"); }
                          ev.target.value = "";
                        }} />
                      </label>
                    </div>
                  </div>
                ))}
                {stdMenus.length === 0 && <p className="text-sm text-center py-8" style={{ color: '#6B6A65' }}>Crea tu primer menú</p>}
                {/* Download template */}
                <button onClick={() => {
                  const header = "unique_code\tprice\tsection\n";
                  const ex = products.slice(0, 2).map(p => `${p.unique_code}\t${p.base_price}\tEntradas`).join("\n");
                  const blob = new Blob([header + ex], { type: "text/tab-separated-values" });
                  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "template_menu_import.xls"; a.click();
                }} className="w-full text-[11px] px-3 py-2 rounded-md border hover:bg-gray-50 flex items-center gap-2 justify-center" style={{ borderColor: '#E6E4DD', color: '#6B6A65' }}>
                  <Download className="h-3 w-3" /> Template Menú
                </button>
              </div>
              {/* RIGHT: Menu content */}
              <div className="flex-1 border rounded-lg p-4" style={{ borderColor: '#E6E4DD', background: 'white' }}>
                {!selectedMenuId ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <LayoutGrid className="h-12 w-12 mx-auto mb-3" style={{ color: '#A89F9A' }} />
                      <p className="text-sm" style={{ color: '#6B6A65' }}>Selecciona un menú para ver su contenido</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-heading font-bold" style={{ color: '#2A2A2A' }}>
                        {stdMenus.find(m => m.id === selectedMenuId)?.name}
                      </h3>
                      <Button size="sm" onClick={openNewCat} style={{ background: '#C05C46' }}>
                        <Plus className="h-3 w-3 mr-1" /> Sección
                      </Button>
                    </div>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="categories" type="CATEGORY">
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            {categories.sort((a, b) => a.order - b.order).map((cat, index) => (
                              <Draggable key={cat.id} draggableId={`cat-${cat.id}`} index={index}>
                                {(provided, snapshot) => (
                                  <div ref={provided.innerRef} {...provided.draggableProps}
                                    className={`mb-2 border rounded-lg ${snapshot.isDragging ? "shadow-lg" : ""}`}
                                    style={{ ...provided.draggableProps.style, borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                                    <div className="flex items-center gap-2 p-3">
                                      <div {...provided.dragHandleProps} className="drag-handle"><GripVertical className="h-4 w-4" style={{ color: '#A89F9A' }} /></div>
                                      <div className="flex items-center gap-2">
                                        {cat.image_path || cat.image_url ? (
                                          <img src={cat.image_path ? imgUrl(cat.image_path) : cat.image_url} alt="" className="w-8 h-8 rounded-md object-cover" />
                                        ) : (
                                          <span className="text-xl">{cat.icon}</span>
                                        )}
                                      </div>
                                      <div className="flex-1 cursor-pointer" onClick={() => toggleCat(cat.id)}>
                                        <div className="font-heading font-bold text-sm" style={{ color: '#2A2A2A' }}>{cat.name}</div>
                                        {cat.subtitle && <div className="text-xs" style={{ color: '#C05C46' }}>{cat.subtitle}</div>}
                                        <div className="text-[10px]" style={{ color: '#6B6A65' }}>{items.filter(i => i.category_id === cat.id).length} productos</div>
                                      </div>
                                      <Button size="sm" variant="ghost" onClick={() => openNewItem(cat.id)}><Plus className="h-3 w-3" /></Button>
                                      <Button size="sm" variant="ghost" onClick={() => openEditCat(cat)}><Pencil className="h-3 w-3" /></Button>
                                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteCat(cat.id)}><Trash2 className="h-3 w-3" /></Button>
                                      {expandedCats[cat.id] ? <ChevronDown className="h-3 w-3" style={{ color: '#6B6A65' }} /> : <ChevronRight className="h-3 w-3" style={{ color: '#6B6A65' }} />}
                                    </div>
                                    {expandedCats[cat.id] && (
                                      <Droppable droppableId={cat.id} type="ITEM">
                                        {(provided) => (
                                          <div ref={provided.innerRef} {...provided.droppableProps} className="px-3 pb-3 space-y-1">
                                            {items.filter(i => i.category_id === cat.id).sort((a, b) => a.order - b.order).map((item, idx) => (
                                              <Draggable key={item.id} draggableId={`item-${item.id}`} index={idx}>
                                                {(provided, snapshot) => (
                                                  <div ref={provided.innerRef} {...provided.draggableProps}
                                                    className={`flex items-center gap-2 p-2 rounded border ${snapshot.isDragging ? "shadow-md" : ""}`}
                                                    style={{ ...provided.draggableProps.style, borderColor: '#E6E4DD', background: 'white' }}>
                                                    <div {...provided.dragHandleProps} className="drag-handle"><GripVertical className="h-3 w-3" style={{ color: '#A89F9A' }} /></div>
                                                    <ItemVisual icon={item.icon} imagePath={item.image_path} size="text-lg" imgClass="w-6 h-6" />
                                                    <div className="flex-1 min-w-0">
                                                      <span className="font-medium text-xs truncate block" style={{ color: '#2A2A2A' }}>{item.name}</span>
                                                    </div>
                                                    <span className="font-bold text-xs whitespace-nowrap" style={{ color: '#C05C46' }}>{fmtPrice(item.price)}</span>
                                                    <Button size="sm" variant="ghost" className="text-red-500 h-6 w-6 p-0" onClick={() => deleteItem(item.menu_product_id || item.id)}><Trash2 className="h-3 w-3" /></Button>
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                    {categories.length === 0 && <p className="text-center py-8 text-sm" style={{ color: '#6B6A65' }}>Este menú está vacío. Importa contenido o agrega secciones.</p>}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === "users" && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Usuarios</h2>
              <Button onClick={() => { setUserForm({ email: "", name: "", role: "restaurant" }); setShowUserDialog(true); }}
                style={{ background: '#C05C46' }}><Plus className="h-4 w-4 mr-2" /> Nuevo Usuario</Button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-2.5 h-4 w-4" style={{ color: '#A89F9A' }} />
              <input type="text" placeholder="Buscar por nombre o email..." value={usersSearch}
                onChange={e => { setUsersSearch(e.target.value); setUsersPage(1); }}
                className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm" style={{ borderColor: '#E6E4DD' }} />
            </div>
            <div className="bg-white border rounded-lg overflow-hidden mb-4" style={{ borderColor: '#E6E4DD' }}>
              <table className="w-full">
                <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Email</th>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Nombre</th>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Rol</th>
                  <th className="text-center p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Estado</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Acciones</th>
                </tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b last:border-0" style={{ borderColor: '#E6E4DD', opacity: u.is_active === false ? 0.5 : 1 }}>
                      <td className="p-3 text-sm">{u.email}</td>
                      <td className="p-3 text-sm">{u.name}</td>
                      <td className="p-3"><span className="text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ background: u.role === 'admin' ? '#C05C4620' : '#4A7c5920', color: u.role === 'admin' ? '#C05C46' : '#4A7c59' }}>{u.role}</span></td>
                      <td className="p-3 text-center">
                        <Switch checked={u.is_active !== false} onCheckedChange={async () => {
                          await api.admin.toggleUserActive(u.id); refreshUsers();
                        }} disabled={u.email === user?.email} />
                      </td>
                      <td className="p-3 text-right">
                        {u.email !== user?.email && (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => { setAccessUser(u); setAccessRestIds(u.restaurant_ids || []); setShowAccessDialog(true); }} title="Accesos">
                              <Store className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => impersonateUser(u)} title="Ingresar como">
                              <UserCheck className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                              if (window.confirm(`¿Eliminar permanentemente a ${u.email}?`)) { await api.admin.deleteUser(u.id); refreshUsers(); toast.success("Usuario eliminado"); }
                            }} title="Eliminar"><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: '#6B6A65' }}>No hay usuarios</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: '#6B6A65' }}>{usersTotal} usuarios</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)}><ChevronLeft className="h-3 w-3" /></Button>
                <span className="text-xs" style={{ color: '#6B6A65' }}>Página {usersPage} de {usersTotalPages}</span>
                <Button variant="outline" size="sm" disabled={usersPage >= usersTotalPages} onClick={() => setUsersPage(p => p + 1)}><ChevronRight className="h-3 w-3" /></Button>
              </div>
            </div>
          </div>
        )}

        {/* SMTP Tab */}
        {tab === "smtp" && (
          <div className="animate-fade-in">
            <h2 className="text-2xl font-heading font-bold mb-6" style={{ color: '#2A2A2A' }}>Configuración SMTP</h2>
            <div className="bg-white border rounded-lg p-6 space-y-4 max-w-2xl" style={{ borderColor: '#E6E4DD' }}>
              <div className="flex items-center justify-between">
                <Label className="font-semibold">SMTP Habilitado</Label>
                <Switch checked={smtpForm.enabled} onCheckedChange={v => setSmtpForm(p => ({...p, enabled: v}))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs">Host SMTP</Label><Input value={smtpForm.host} onChange={e => setSmtpForm(p => ({...p, host: e.target.value}))} placeholder="smtp.gmail.com" /></div>
                <div className="space-y-2"><Label className="text-xs">Puerto</Label><Input type="number" value={smtpForm.port} onChange={e => setSmtpForm(p => ({...p, port: Number(e.target.value)}))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs">Usuario</Label><Input value={smtpForm.username} onChange={e => setSmtpForm(p => ({...p, username: e.target.value}))} /></div>
                <div className="space-y-2"><Label className="text-xs">Contraseña</Label><Input type="password" value={smtpForm.password} onChange={e => setSmtpForm(p => ({...p, password: e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label className="text-xs">Seguridad</Label>
                  <select value={smtpForm.security} onChange={e => setSmtpForm(p => ({...p, security: e.target.value}))}
                    className="w-full text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}>
                    <option value="TLS">TLS</option><option value="SSL">SSL</option><option value="STARTTLS">STARTTLS</option><option value="NONE">Ninguna</option>
                  </select></div>
                <div className="space-y-2"><Label className="text-xs">Email remitente</Label><Input value={smtpForm.sender_email} onChange={e => setSmtpForm(p => ({...p, sender_email: e.target.value}))} placeholder="noreply@empresa.com" /></div>
                <div className="space-y-2"><Label className="text-xs">Nombre remitente</Label><Input value={smtpForm.sender_name} onChange={e => setSmtpForm(p => ({...p, sender_name: e.target.value}))} /></div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={async () => {
                  try { await api.admin.updateSmtp(smtpForm); toast.success("Configuración SMTP guardada"); } catch (e) { toast.error("Error al guardar"); }
                }} style={{ background: '#C05C46' }}>Guardar</Button>
                <Button variant="outline" disabled={smtpTesting} onClick={async () => {
                  setSmtpTesting(true);
                  try { await api.admin.updateSmtp(smtpForm); const { data } = await api.admin.testSmtp(); toast.success(data.message); }
                  catch (e) { toast.error(e.response?.data?.detail || "Error de conexión"); }
                  setSmtpTesting(false);
                }}>{smtpTesting ? "Probando..." : "Probar Conexión"}</Button>
              </div>
              <p className="text-xs" style={{ color: '#6B6A65' }}>Cuando SMTP está habilitado, los códigos de verificación se envían por email. Cuando está deshabilitado, se muestran en pantalla.</p>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {tab === "logs" && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Logs de Cambios de Precios</h2>
              <span className="text-sm" style={{ color: '#6B6A65' }}>{logsTotal} registros</span>
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-4 flex-wrap items-end">
              <div className="space-y-1">
                <label className="text-xs font-semibold" style={{ color: '#6B6A65' }}>Desde</label>
                <input type="date" value={logsDateFrom} onChange={e => { setLogsDateFrom(e.target.value); setLogsPage(1); }}
                  className="text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }} data-testid="logs-date-from" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold" style={{ color: '#6B6A65' }}>Hasta</label>
                <input type="date" value={logsDateTo} onChange={e => { setLogsDateTo(e.target.value); setLogsPage(1); }}
                  className="text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }} data-testid="logs-date-to" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold" style={{ color: '#6B6A65' }}>Por página</label>
                <select value={logsLimit} onChange={e => { setLogsLimit(Number(e.target.value)); setLogsPage(1); }}
                  className="text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}>
                  <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                </select>
              </div>
              {(logsDateFrom || logsDateTo) && (
                <button onClick={() => { setLogsDateFrom(""); setLogsDateTo(""); setLogsPage(1); }}
                  className="text-xs px-3 py-2 rounded-md" style={{ color: '#C05C46', background: '#C05C4610' }}>
                  Limpiar filtros
                </button>
              )}
            </div>

            <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: '#E6E4DD' }}>
              <table className="w-full">
                <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Menú</th>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Usuario</th>
                  <th className="text-left p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Producto</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Precio Base</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Anterior</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Nuevo</th>
                  <th className="text-right p-3 text-xs font-body font-semibold uppercase" style={{ color: '#6B6A65' }}>Fecha</th>
                </tr></thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b last:border-0" style={{ borderColor: '#E6E4DD' }}>
                      <td className="p-3"><span className="text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ background: '#C05C4620', color: '#C05C46' }}>
                        {l.menu_type || '-'}</span></td>
                      <td className="p-3 text-sm font-body">{l.user_email}</td>
                      <td className="p-3 text-sm font-body font-medium">{l.item_name}</td>
                      <td className="p-3 text-sm text-right" style={{ color: '#6B6A65' }}>{l.base_price != null ? fmtPrice(l.base_price) : "-"}</td>
                      <td className="p-3 text-sm text-right" style={{ color: '#6B6A65' }}>{l.old_price != null ? fmtPrice(l.old_price) : "-"}</td>
                      <td className="p-3 text-sm text-right font-bold" style={{ color: '#C05C46' }}>{fmtPrice(l.new_price)}</td>
                      <td className="p-3 text-xs text-right" style={{ color: '#6B6A65' }}>{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-sm" style={{ color: '#6B6A65' }}>No hay registros de cambios de precios</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <Button variant="outline" size="sm" disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm" style={{ color: '#6B6A65' }}>Página {logsPage} de {logsTotalPages}</span>
              <Button variant="outline" size="sm" disabled={logsPage >= logsTotalPages} onClick={() => setLogsPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Restaurant Dialog */}
      <Dialog open={showRestDialog} onOpenChange={setShowRestDialog}>
        <DialogContent data-testid="restaurant-dialog" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editRest ? "Editar Restaurante" : "Nuevo Restaurante"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-2"><Label>Nombre</Label><Input value={restForm.name} onChange={e => setRestForm(p => ({...p, name: e.target.value}))} data-testid="rest-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Slug (URL)</Label><Input value={restForm.slug} onChange={e => setRestForm(p => ({...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")}))} data-testid="rest-slug-input" /></div>
              <div className="space-y-2"><Label>Email del propietario</Label><Input type="email" value={restForm.owner_email} onChange={e => setRestForm(p => ({...p, owner_email: e.target.value}))} data-testid="rest-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo</Label><Input value={restForm.description} onChange={e => setRestForm(p => ({...p, description: e.target.value}))} placeholder="Ciudad grande, Ciudad media, etc." /></div>
              <div className="space-y-2"><Label>Ciudad</Label><Input value={restForm.city} onChange={e => setRestForm(p => ({...p, city: e.target.value}))} placeholder="Bogotá, Medellín, etc." /></div>
            </div>

            {editRest && (<>
              {/* Menu Assignment */}
              <div className="border-t pt-4" style={{ borderColor: '#E6E4DD' }}>
                <h4 className="font-heading font-bold text-sm mb-3" style={{ color: '#2A2A2A' }}>Asignación de Menús</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Menú Principal</Label>
                    <select value={restForm.main_menu_id} onChange={e => setRestForm(p => ({...p, main_menu_id: e.target.value}))}
                      className="w-full text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}><option value="">Sin asignar</option>
                      {stdMenus.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
                  <div className="space-y-2"><Label>Menú Happy Hour</Label>
                    <select value={restForm.happy_hour_menu_id} onChange={e => setRestForm(p => ({...p, happy_hour_menu_id: e.target.value}))}
                      className="w-full text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}><option value="">Sin asignar</option>
                      {stdMenus.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
                </div>
              </div>

              {/* Template & Logo */}
              <div className="border-t pt-4" style={{ borderColor: '#E6E4DD' }}>
                <h4 className="font-heading font-bold text-sm mb-3" style={{ color: '#2A2A2A' }}>Plantilla y Logo</h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-2"><Label>Plantilla</Label>
                    <select value={restForm.template_slug} onChange={e => setRestForm(p => ({...p, template_slug: e.target.value}))}
                      className="w-full text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}>
                      <option value="classic">Clásico</option><option value="modern">Moderno</option></select></div>
                  <div className="space-y-2"><Label>Logo (ruta almacenada)</Label>
                    <Input value={restForm.logo_url} onChange={e => setRestForm(p => ({...p, logo_url: e.target.value}))} placeholder="Subir desde panel restaurante" className="text-xs" /></div>
                </div>
              </div>

              {/* Colors & Font */}
              <div className="border-t pt-4" style={{ borderColor: '#E6E4DD' }}>
                <h4 className="font-heading font-bold text-sm mb-3" style={{ color: '#2A2A2A' }}>Colores y Fuente</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Principal</Label>
                    <div className="flex gap-1"><input type="color" value={restForm.primary_color} onChange={e => setRestForm(p => ({...p, primary_color: e.target.value}))} className="w-8 h-8 rounded border cursor-pointer" style={{ borderColor: '#E6E4DD' }} />
                    <Input value={restForm.primary_color} onChange={e => setRestForm(p => ({...p, primary_color: e.target.value}))} className="flex-1 text-xs" /></div></div>
                  <div className="space-y-1"><Label className="text-xs">Acento</Label>
                    <div className="flex gap-1"><input type="color" value={restForm.accent_color} onChange={e => setRestForm(p => ({...p, accent_color: e.target.value}))} className="w-8 h-8 rounded border cursor-pointer" style={{ borderColor: '#E6E4DD' }} />
                    <Input value={restForm.accent_color} onChange={e => setRestForm(p => ({...p, accent_color: e.target.value}))} className="flex-1 text-xs" /></div></div>
                  <div className="space-y-1"><Label className="text-xs">Fondo</Label>
                    <div className="flex gap-1"><input type="color" value={restForm.bg_color} onChange={e => setRestForm(p => ({...p, bg_color: e.target.value}))} className="w-8 h-8 rounded border cursor-pointer" style={{ borderColor: '#E6E4DD' }} />
                    <Input value={restForm.bg_color} onChange={e => setRestForm(p => ({...p, bg_color: e.target.value}))} className="flex-1 text-xs" /></div></div>
                  <div className="space-y-1"><Label className="text-xs">Fuente</Label>
                    <select value={restForm.font_family} onChange={e => setRestForm(p => ({...p, font_family: e.target.value}))}
                      className="w-full text-xs p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}>
                      {["Manrope","Inter","Lato","Open Sans","Poppins","Playfair Display","Cormorant Garamond","Montserrat"].map(f => <option key={f} value={f}>{f}</option>)}</select></div>
                </div>
              </div>

              {/* GTM & SEO */}
              <div className="border-t pt-4" style={{ borderColor: '#E6E4DD' }}>
                <h4 className="font-heading font-bold text-sm mb-3" style={{ color: '#2A2A2A' }}>GTM y SEO</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Google Tag Manager</Label><Input value={restForm.gtm_container_id} onChange={e => setRestForm(p => ({...p, gtm_container_id: e.target.value}))} placeholder="GTM-XXXXXXX" /></div>
                  <div className="space-y-2 flex items-center gap-3 pt-6">
                    <Switch checked={restForm.seo_index} onCheckedChange={v => setRestForm(p => ({...p, seo_index: v}))} />
                    <Label className="text-xs">Indexable en buscadores</Label>
                  </div>
                </div>
              </div>

              {/* Happy Hour Schedule */}
              <div className="border-t pt-4" style={{ borderColor: '#E6E4DD' }}>
                <h4 className="font-heading font-bold text-sm mb-3" style={{ color: '#2A2A2A' }}>Horario Happy Hour</h4>
                <div className="flex gap-1.5 flex-wrap mb-3">
                  {[{k:"lun",l:"L"},{k:"mar",l:"M"},{k:"mie",l:"Mi"},{k:"jue",l:"J"},{k:"vie",l:"V"},{k:"sab",l:"S"},{k:"dom",l:"D"}].map(d => (
                    <button key={d.k} type="button" onClick={() => {
                      const days = restForm.happy_hour_schedule?.days || [];
                      setRestForm(p => ({...p, happy_hour_schedule: {...p.happy_hour_schedule, days: days.includes(d.k) ? days.filter(x => x !== d.k) : [...days, d.k]}}));
                    }} className="w-8 h-8 rounded-lg text-xs font-bold" style={{
                      background: (restForm.happy_hour_schedule?.days || []).includes(d.k) ? '#C05C46' : 'white',
                      color: (restForm.happy_hour_schedule?.days || []).includes(d.k) ? 'white' : '#6B6A65',
                      border: `1px solid ${(restForm.happy_hour_schedule?.days || []).includes(d.k) ? '#C05C46' : '#E6E4DD'}`
                    }}>{d.l}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1"><Label className="text-xs">Hora inicio</Label>
                    <Input type="time" value={restForm.happy_hour_schedule?.start_time || ""} onChange={e => setRestForm(p => ({...p, happy_hour_schedule: {...p.happy_hour_schedule, start_time: e.target.value}}))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Hora fin</Label>
                    <Input type="time" value={restForm.happy_hour_schedule?.end_time || ""} onChange={e => setRestForm(p => ({...p, happy_hour_schedule: {...p.happy_hour_schedule, end_time: e.target.value}}))} /></div>
                </div>
              </div>
            </>)}
          </div>
          <DialogFooter><Button onClick={saveRest} style={{ background: '#C05C46' }} data-testid="save-restaurant-btn">{editRest ? "Guardar" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent data-testid="category-dialog" onInteractOutside={(e) => e.preventDefault()} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editCat ? "Editar Sección" : "Nueva Sección"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Título</Label><Input value={catForm.name} onChange={e => setCatForm(p => ({...p, name: e.target.value}))} placeholder="Entradas" data-testid="cat-name-input" /></div>
            <div className="space-y-2"><Label>Subtítulo</Label><Input value={catForm.subtitle} onChange={e => setCatForm(p => ({...p, subtitle: e.target.value}))} placeholder="Para Comenzar" /></div>
            <div className="space-y-2"><Label>Icono (emoji)</Label>
              <div className="flex gap-2 items-center">
                <Input value={catForm.icon} onChange={e => setCatForm(p => ({...p, icon: e.target.value}))} placeholder="🍽️" className="flex-1" data-testid="cat-icon-input" />
                <button type="button" onClick={() => setShowEmojiPicker(showEmojiPicker === "cat" ? false : "cat")}
                  className="flex items-center justify-center w-9 h-9 rounded-md border hover:bg-gray-50" style={{ borderColor: showEmojiPicker === "cat" ? '#C05C46' : '#E6E4DD' }} title="Seleccionar emoji" data-testid="cat-emoji-picker">
                  <Info className="h-4 w-4" style={{ color: '#C05C46' }} />
                </button>
              </div>
              {showEmojiPicker === "cat" && (
                <InlineEmojiPicker onSelect={(em) => { setCatForm(p => ({...p, icon: em})); setShowEmojiPicker(false); }} />
              )}
            </div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={catForm.description} onChange={e => setCatForm(p => ({...p, description: e.target.value}))} /></div>
            {/* Image */}
            <div className="space-y-2">
              <Label>Imagen de la sección</Label>
              <div className="flex items-center gap-3">
                {(catForm.image_path || catForm.image_url) ? (
                  <div className="relative">
                    <img src={catForm.image_path ? imgUrl(catForm.image_path) : catForm.image_url} alt="Preview"
                      className="w-24 h-16 rounded-lg object-cover border" style={{ borderColor: '#E6E4DD' }} />
                    <button type="button" onClick={() => setCatForm(p => ({...p, image_path: "", image_url: ""}))}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center bg-red-500 text-white">
                      <X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-24 h-16 rounded-lg border-2 border-dashed cursor-pointer hover:border-[#C05C46] transition-colors"
                    style={{ borderColor: '#E6E4DD' }}>
                    <ImagePlus className="h-5 w-5" style={{ color: '#A89F9A' }} />
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      try {
                        const { data } = await api.upload(fd);
                        setCatForm(p => ({...p, image_path: data.path, image_url: ""}));
                        toast.success("Imagen subida");
                      } catch (err) { toast.error("Error al subir imagen"); }
                    }} />
                  </label>
                )}
                <div className="flex-1 space-y-1">
                  <Input value={catForm.image_url} onChange={e => setCatForm(p => ({...p, image_url: e.target.value, image_path: ""}))}
                    placeholder="https://... o sube una imagen" className="text-xs" />
                  <p className="text-[10px]" style={{ color: '#6B6A65' }}>URL de imagen o sube un archivo</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveCat} style={{ background: '#C05C46' }} data-testid="save-category-btn">{editCat ? "Guardar" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent data-testid="item-dialog" className="max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-heading">{editItem ? "Editar Item" : "Nuevo Item"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={itemForm.name} onChange={e => setItemForm(p => ({...p, name: e.target.value}))} data-testid="item-name-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={itemForm.description} onChange={e => setItemForm(p => ({...p, description: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Precio (COP)</Label><Input type="number" value={itemForm.price} onChange={e => setItemForm(p => ({...p, price: e.target.value}))} data-testid="item-price-input" /></div>
              <div className="space-y-2"><Label>Icono (emoji)</Label>
                <div className="flex gap-2 items-center">
                  <Input value={itemForm.icon} onChange={e => setItemForm(p => ({...p, icon: e.target.value}))} placeholder="🍽️" className="flex-1" />
                  <button type="button" onClick={() => setShowEmojiPicker(showEmojiPicker === "item" ? false : "item")}
                    className="flex items-center justify-center w-9 h-9 rounded-md border hover:bg-gray-50" style={{ borderColor: showEmojiPicker === "item" ? '#C05C46' : '#E6E4DD' }} title="Seleccionar emoji" data-testid="item-emoji-picker">
                    <Info className="h-4 w-4" style={{ color: '#C05C46' }} />
                  </button>
                </div>
                {showEmojiPicker === "item" && (
                  <InlineEmojiPicker onSelect={(em) => { setItemForm(p => ({...p, icon: em})); setShowEmojiPicker(false); }} />
                )}
              </div>
            </div>
            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Imagen del producto</Label>
              <div className="flex items-center gap-3">
                {itemForm.image_path ? (
                  <div className="relative">
                    <img src={imgUrl(itemForm.image_path)} alt="Preview" className="w-20 h-20 rounded-lg object-cover border" style={{ borderColor: '#E6E4DD' }} />
                    <button onClick={() => setItemForm(p => ({...p, image_path: ""}))}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center bg-red-500 text-white"
                      data-testid="remove-image-btn"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-20 h-20 rounded-lg border-2 border-dashed cursor-pointer hover:border-[#C05C46] transition-colors"
                    style={{ borderColor: '#E6E4DD' }} data-testid="upload-image-label">
                    {uploadingImage ? <span className="text-xs animate-pulse" style={{ color: '#6B6A65' }}>Subiendo...</span> : <ImagePlus className="h-6 w-6" style={{ color: '#A89F9A' }} />}
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="upload-image-input" />
                  </label>
                )}
                <div className="text-xs" style={{ color: '#6B6A65' }}>
                  {itemForm.image_path ? "Imagen cargada. Click X para remover y usar emoji." : "Sube una imagen o usa un emoji/icono en el campo de arriba."}
                </div>
              </div>
            </div>
            <div className="space-y-2"><Label>Alérgenos (separados por coma)</Label><Input value={itemForm.allergens} onChange={e => setItemForm(p => ({...p, allergens: e.target.value}))} placeholder="Gluten, Lacteos" /></div>
            <div className="space-y-2"><Label>Badges (separados por coma)</Label><Input value={itemForm.badges} onChange={e => setItemForm(p => ({...p, badges: e.target.value}))} placeholder="Popular, Nuevo" /></div>
          </div>
          <DialogFooter><Button onClick={saveItem} style={{ background: '#C05C46' }} data-testid="save-item-btn">{editItem ? "Guardar" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Management Dialog */}
      <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
        <DialogContent data-testid="access-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Accesos de {accessUser?.name || accessUser?.email}</DialogTitle>
          </DialogHeader>
          <p className="text-sm mb-4" style={{ color: '#6B6A65' }}>Selecciona los restaurantes a los que este usuario tendra acceso:</p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {restaurants.map(r => (
              <label key={r.id} className="flex items-center gap-3 p-3 rounded-md border cursor-pointer hover:bg-gray-50"
                style={{ borderColor: accessRestIds.includes(r.id) ? '#C05C46' : '#E6E4DD' }}>
                <input type="checkbox" checked={accessRestIds.includes(r.id)}
                  onChange={e => {
                    if (e.target.checked) setAccessRestIds(prev => [...prev, r.id]);
                    else setAccessRestIds(prev => prev.filter(id => id !== r.id));
                  }}
                  className="rounded" />
                <div>
                  <div className="font-medium text-sm" style={{ color: '#2A2A2A' }}>{r.name}</div>
                  <div className="text-xs" style={{ color: '#6B6A65' }}>/{r.slug}</div>
                </div>
              </label>
            ))}
            {restaurants.length === 0 && <p className="text-sm text-center py-4" style={{ color: '#6B6A65' }}>No hay restaurantes creados</p>}
          </div>
          <DialogFooter>
            <Button onClick={async () => {
              try {
                await api.admin.updateUserRestaurants(accessUser.id, accessRestIds);
                toast.success("Accesos actualizados");
                setShowAccessDialog(false);
                loadData();
              } catch (e) { toast.error("Error al actualizar accesos"); }
            }} style={{ background: '#C05C46' }} data-testid="save-access-btn">
              Guardar Accesos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standard Menu Dialog */}
      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent data-testid="menu-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editMenu ? "Editar Menú Estándar" : "Nuevo Menú Estándar"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={menuForm.name} onChange={e => setMenuForm(p => ({...p, name: e.target.value}))} placeholder="Menú Principal, Happy Hour, etc." data-testid="menu-name-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={menuForm.description} onChange={e => setMenuForm(p => ({...p, description: e.target.value}))} /></div>
            <div className="space-y-2"><Label>Código Único</Label><Input value={menuForm.unique_code || ""} onChange={e => setMenuForm(p => ({...p, unique_code: e.target.value.toUpperCase()}))} placeholder="Auto-generado" /></div>
          </div>
          <DialogFooter><Button onClick={async () => {
            try {
              if (editMenu) await api.admin.updateStandardMenu(editMenu.id, menuForm);
              else await api.admin.createStandardMenu(menuForm);
              toast.success(editMenu ? "Menu actualizado" : "Menu creado");
              setShowMenuDialog(false);
              loadData();
            } catch (e) { toast.error("Error"); }
          }} style={{ background: '#C05C46' }} data-testid="save-menu-btn">{editMenu ? "Guardar" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent data-testid="user-dialog" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-heading">Nuevo Usuario</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={userForm.email} onChange={e => setUserForm(p => ({...p, email: e.target.value}))} placeholder="usuario@email.com" data-testid="user-email-input" /></div>
            <div className="space-y-2"><Label>Nombre</Label><Input value={userForm.name} onChange={e => setUserForm(p => ({...p, name: e.target.value}))} placeholder="Nombre del usuario" data-testid="user-name-input" /></div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <select value={userForm.role} onChange={e => setUserForm(p => ({...p, role: e.target.value}))}
                className="w-full text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }} data-testid="user-role-select">
                <option value="restaurant">Restaurante</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
          <DialogFooter><Button onClick={async () => {
            try {
              await api.admin.createUser(userForm);
              toast.success("Usuario creado");
              setShowUserDialog(false);
              loadData();
            } catch (e) { toast.error(e.response?.data?.detail || "Error al crear usuario"); }
          }} style={{ background: '#C05C46' }} data-testid="save-user-btn">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent data-testid="product-dialog" onInteractOutside={(e) => e.preventDefault()} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editProduct ? "Editar Producto" : "Nuevo Producto"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={productForm.name} onChange={e => setProductForm(p => ({...p, name: e.target.value}))} data-testid="product-name-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={productForm.description} onChange={e => setProductForm(p => ({...p, description: e.target.value}))} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Unidades</Label><Input value={productForm.product_units} onChange={e => setProductForm(p => ({...p, product_units: e.target.value}))} placeholder="4 uds, 150g, etc." /></div>
              <div className="space-y-2"><Label>Precio Base (COP)</Label><Input type="number" value={productForm.base_price} onChange={e => setProductForm(p => ({...p, base_price: e.target.value}))} data-testid="product-price-input" /></div>
              <div className="space-y-2"><Label>Código Único</Label><Input value={productForm.unique_code} onChange={e => setProductForm(p => ({...p, unique_code: e.target.value.toUpperCase()}))} placeholder="Auto-generado" /></div>
            </div>
            <div className="space-y-2"><Label>Icono (emoji)</Label>
              <div className="flex gap-2 items-center">
                <Input value={productForm.icon} onChange={e => setProductForm(p => ({...p, icon: e.target.value}))} placeholder="🍽️" className="flex-1" />
                <button type="button" onClick={() => setShowEmojiPicker(showEmojiPicker === "product" ? false : "product")}
                  className="flex items-center justify-center w-9 h-9 rounded-md border hover:bg-gray-50" style={{ borderColor: showEmojiPicker === "product" ? '#C05C46' : '#E6E4DD' }}>
                  <Info className="h-4 w-4" style={{ color: '#C05C46' }} /></button>
              </div>
              {showEmojiPicker === "product" && <InlineEmojiPicker onSelect={(em) => { setProductForm(p => ({...p, icon: em})); setShowEmojiPicker(false); }} />}
            </div>
            <div className="space-y-2"><Label>Alérgenos (separados por coma)</Label><Input value={productForm.allergens} onChange={e => setProductForm(p => ({...p, allergens: e.target.value}))} placeholder="Gluten, Lacteos" /></div>
            <div className="space-y-2"><Label>Badges (separados por coma)</Label><Input value={productForm.badges} onChange={e => setProductForm(p => ({...p, badges: e.target.value}))} placeholder="Popular, Nuevo" /></div>
            {/* Product Image */}
            <div className="space-y-2">
              <Label>Imagen del producto</Label>
              <div className="flex items-center gap-3">
                {(productForm.image_path || productForm.image_url) ? (
                  <div className="relative">
                    <img src={productForm.image_path ? imgUrl(productForm.image_path) : productForm.image_url} alt="Preview"
                      className="w-20 h-20 rounded-lg object-cover border" style={{ borderColor: '#E6E4DD' }} />
                    <button type="button" onClick={() => setProductForm(p => ({...p, image_path: "", image_url: ""}))}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center bg-red-500 text-white">
                      <X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-20 h-20 rounded-lg border-2 border-dashed cursor-pointer hover:border-[#C05C46] transition-colors"
                    style={{ borderColor: '#E6E4DD' }}>
                    <ImagePlus className="h-6 w-6" style={{ color: '#A89F9A' }} />
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      try {
                        const { data } = await api.upload(fd);
                        setProductForm(p => ({...p, image_path: data.path, image_url: ""}));
                        toast.success("Imagen subida");
                      } catch (err) { toast.error("Error al subir imagen"); }
                    }} />
                  </label>
                )}
                <div className="flex-1 space-y-1">
                  <Input value={productForm.image_url} onChange={e => setProductForm(p => ({...p, image_url: e.target.value, image_path: ""}))}
                    placeholder="https://... o sube una imagen" className="text-xs" />
                  <p className="text-[10px]" style={{ color: '#6B6A65' }}>URL de imagen o sube un archivo</p>
                </div>
              </div>
            </div>
            {/* Menu Prices - only when editing */}
            {editProduct && productMenuPrices.length > 0 && (
              <div className="space-y-2">
                <Label className="font-semibold">Precios por Menú</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {productMenuPrices.map(mp => (
                    <div key={mp.id} className="flex items-center gap-3 p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold" style={{ color: '#2A2A2A' }}>{mp.menu_name}</div>
                        <div className="text-[10px]" style={{ color: '#6B6A65' }}>{mp.category_name}</div>
                      </div>
                      <Input type="number" value={mp.menu_price ?? ""} placeholder={String(editProduct.base_price)}
                        onChange={e => setProductMenuPrices(prev => prev.map(p => p.id === mp.id ? {...p, menu_price: e.target.value ? Number(e.target.value) : null} : p))}
                        className="w-28 text-sm text-right" />
                      <button type="button" onClick={() => setProductMenuPrices(prev => prev.map(p => p.id === mp.id ? {...p, menu_price: null} : p))}
                        className="text-[10px] px-2 py-1 rounded border hover:bg-gray-50 whitespace-nowrap" style={{ borderColor: '#E6E4DD', color: '#6B6A65' }}>
                        Usar base
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px]" style={{ color: '#6B6A65' }}>Precio base: {fmtPrice(editProduct.base_price)}. Deja vacío para usar precio base.</p>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={async () => {
            if (!productForm.name.trim()) { toast.error("El nombre es obligatorio"); return; }
            if (!productForm.base_price || Number(productForm.base_price) <= 0) { toast.error("El precio base debe ser mayor a 0"); return; }
            const payload = { ...productForm, name: productForm.name.trim(), base_price: Number(productForm.base_price),
              allergens: productForm.allergens ? productForm.allergens.split(",").map(s => s.trim()).filter(Boolean) : [],
              badges: productForm.badges ? productForm.badges.split(",").map(s => s.trim()).filter(Boolean) : [] };
            try {
              if (editProduct) {
                await api.admin.updateProduct(editProduct.id, payload);
                // Batch save all menu prices
                if (productMenuPrices.length > 0) {
                  await api.admin.batchUpdateMenuPrices(editProduct.id,
                    productMenuPrices.map(mp => ({ menu_product_id: mp.id, menu_price: mp.menu_price }))
                  );
                }
              } else {
                await api.admin.createProduct(payload);
              }
              toast.success(editProduct ? "Producto actualizado" : "Producto creado");
              setShowProductDialog(false); loadData();
            } catch (e) { toast.error("Error"); }
          }} style={{ background: '#C05C46' }} data-testid="save-product-btn">{editProduct ? "Guardar Todo" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Product to Menu Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent data-testid="assign-dialog" className="max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Agregar Producto a Seccion</DialogTitle></DialogHeader>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-3 h-4 w-4" style={{ color: '#A89F9A' }} />
            <input type="text" placeholder="Buscar producto..." value={assignSearch} onChange={e => setAssignSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm" style={{ borderColor: '#E6E4DD' }} />
          </div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {products.filter(p => !assignSearch || p.name.toLowerCase().includes(assignSearch.toLowerCase())).map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                style={{ borderColor: '#E6E4DD' }}
                onClick={async () => {
                  try {
                    await api.admin.assignProduct(selectedMenuId, { product_id: p.id, category_id: assignCatId });
                    toast.success(`${p.name} agregado`);
                    setShowAssignDialog(false);
                    loadMenuContent(selectedMenuId);
                  } catch (e) { toast.error("Error"); }
                }}>
                <span className="text-xl">{p.icon || "📦"}</span>
                <div className="flex-1">
                  <div className="font-medium text-sm" style={{ color: '#2A2A2A' }}>{p.name}</div>
                  <div className="text-xs" style={{ color: '#6B6A65' }}>{p.description}</div>
                </div>
                <span className="font-bold text-sm" style={{ color: '#C05C46' }}>{fmtPrice(p.base_price)}</span>
              </div>
            ))}
            {products.length === 0 && <p className="text-center py-4 text-sm" style={{ color: '#6B6A65' }}>Crea productos primero en la seccion Productos</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Products Import Dialog */}
      <Dialog open={showBulkProductsDialog} onOpenChange={(v) => { if (!bulkApplying) setShowBulkProductsDialog(v); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="font-heading">Vista Previa - Importación de Productos</DialogTitle>
          </DialogHeader>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6">
            {bulkProductsPreview && (
              <div className="space-y-3 pb-2">
                <div className="flex justify-between items-center">
                  <p className="text-sm" style={{ color: '#6B6A65' }}>
                    {bulkProductsPreview.filter(r => r.action === "create").length} nuevos,{" "}
                    {bulkProductsPreview.filter(r => r.action === "update").length} actualizaciones,{" "}
                    {bulkProductsPreview.filter(r => r.action === "ignore").length} ignorados,{" "}
                    {bulkProductsPreview.length} total
                  </p>
                  {/* Bulk actions for existing products */}
                  {bulkProductsPreview.some(r => r.existing_id) && !bulkApplying && (
                    <div className="flex gap-2">
                      <button onClick={() => setBulkProductsPreview(prev => prev.map(r => r.existing_id ? {...r, action: "update"} : r))}
                        className="text-[10px] px-3 py-1 rounded-md border hover:bg-blue-50" style={{ borderColor: '#E6E4DD', color: '#3B82F6' }}>
                        Actualizar todos los existentes
                      </button>
                      <button onClick={() => setBulkProductsPreview(prev => prev.map(r => r.existing_id ? {...r, action: "ignore"} : r))}
                        className="text-[10px] px-3 py-1 rounded-md border hover:bg-gray-50" style={{ borderColor: '#E6E4DD', color: '#6B6A65' }}>
                        Ignorar todos los existentes
                      </button>
                    </div>
                  )}
                </div>
                <div className="border rounded-lg overflow-x-auto" style={{ borderColor: '#E6E4DD' }}>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Acción</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Código</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Nombre</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Unidades</th>
                      <th className="text-right p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Precio</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Imagen</th>
                      <th className="p-2"></th>
                    </tr></thead>
                    <tbody>
                      {bulkProductsPreview.map((r, idx) => (
                        <tr key={idx} className="border-b last:border-0" style={{ borderColor: '#E6E4DD', opacity: r.action === "ignore" ? 0.4 : 1 }}>
                          <td className="p-2">
                            {r.existing_id ? (
                              <select value={r.action} onChange={e => setBulkProductsPreview(prev => prev.map((p, i) => i === idx ? {...p, action: e.target.value} : p))}
                                className="text-[10px] px-1 py-0.5 rounded border" style={{ borderColor: '#E6E4DD' }} disabled={bulkApplying}>
                                <option value="update">Actualizar</option>
                                <option value="ignore">Ignorar</option>
                              </select>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">Nuevo</span>
                            )}
                          </td>
                          <td className="p-2 text-xs font-mono">{fmtCode(r.unique_code)}</td>
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2 text-xs">{r.product_units}</td>
                          <td className="p-2 text-right font-bold" style={{ color: '#C05C46' }}>{fmtPrice(r.base_price)}</td>
                          <td className="p-2 text-xs truncate max-w-[120px]" title={r.image_url}>{r.image_url ? (r.image_url.includes("drive.google") ? "📥 Drive" : "🔗 URL") : "-"}</td>
                          <td className="p-2">
                            {!bulkApplying && <button onClick={() => setBulkProductsPreview(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {bulkProductsPreview.some(r => r.image_url && r.image_url.includes("drive.google")) && (
                  <p className="text-xs p-2 rounded" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                    Las imágenes de Google Drive se descargarán automáticamente.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Fixed footer */}
          <div className="border-t px-6 py-4 flex items-center justify-between" style={{ borderColor: '#E6E4DD', background: 'white' }}>
            {bulkApplying ? (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex justify-between text-xs" style={{ color: '#6B6A65' }}>
                  <span>Procesando: {bulkProgress.current || "..."}</span>
                  <span>{bulkProgress.done} / {bulkProgress.total}</span>
                </div>
                <div className="w-full h-2.5 rounded-full" style={{ background: '#E6E4DD' }}>
                  <div className="h-2.5 rounded-full transition-all duration-300" style={{ background: '#C05C46', width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total * 100) : 0}%` }} />
                </div>
                <div className="text-[10px] text-center" style={{ color: '#A89F9A' }}>
                  {Math.round(bulkProgress.total ? (bulkProgress.done / bulkProgress.total * 100) : 0)}% completado
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm" style={{ color: '#6B6A65' }}>
                  {bulkProductsPreview ? (() => {
                    const active = bulkProductsPreview.filter(r => r.action !== "ignore");
                    const ignored = bulkProductsPreview.filter(r => r.action === "ignore");
                    return `${active.length} a procesar${ignored.length ? `, ${ignored.length} ignorados` : ""}`;
                  })() : ""}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowBulkProductsDialog(false)}>Cancelar</Button>
                  <Button style={{ background: '#C05C46' }} disabled={!bulkProductsPreview || bulkProductsPreview.filter(r => r.action !== "ignore").length === 0}
                    onClick={async () => {
                      const activeItems = bulkProductsPreview.filter(r => r.action !== "ignore");
                      setBulkApplying(true);
                      const total = activeItems.length;
                      setBulkProgress({ done: 0, total, current: "" });
                      let created = 0, updated = 0, errors = 0;
                      for (let i = 0; i < total; i++) {
                        const item = activeItems[i];
                        setBulkProgress({ done: i, total, current: item.name });
                        try {
                          const { data } = await api.admin.applySingleProduct(item);
                          if (data.action === "create") created++;
                          else updated++;
                        } catch (e) { errors++; }
                        // Wait 1 second between products for Drive rate limiting
                        if (i < total - 1 && item.image_url && item.image_url.includes("drive.google")) {
                          await new Promise(r => setTimeout(r, 1000));
                        }
                      }
                      setBulkProgress({ done: total, total, current: "Completado" });
                      setTimeout(() => {
                        toast.success(`${created} creados, ${updated} actualizados${errors ? `, ${errors} errores` : ""}`);
                        setBulkApplying(false);
                        setShowBulkProductsDialog(false);
                        setBulkProductsPreview(null);
                        loadData();
                      }, 800);
                    }}>
                    Aplicar {bulkProductsPreview ? bulkProductsPreview.filter(r => r.action !== "ignore").length : 0} productos
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Restaurant Import Dialog */}
      <Dialog open={showRestImportDialog} onOpenChange={(v) => { if (!restImportApplying) setShowRestImportDialog(v); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="font-heading">Importar Restaurantes</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6">
            {restImportPreview && restImportPreview.length > 0 && (
              <div className="space-y-3 pb-2">
                <p className="text-sm" style={{ color: '#6B6A65' }}>
                  {restImportPreview.filter(r => r.action === "create" && r.status === "ready").length} nuevos,{" "}
                  {restImportPreview.filter(r => r.action === "update" && r.status === "ready").length} actualizaciones,{" "}
                  {restImportPreview.filter(r => r.status === "error").length} errores
                </p>
                <div className="border rounded-lg overflow-x-auto" style={{ borderColor: '#E6E4DD' }}>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Acción</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Nombre</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Ciudad</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Email</th>
                      <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Slug</th>
                      <th className="p-2"></th>
                    </tr></thead>
                    <tbody>
                      {restImportPreview.map((r, idx) => (
                        <tr key={idx} className="border-b last:border-0" style={{ borderColor: '#E6E4DD', opacity: r.status === "error" ? 0.4 : 1 }}>
                          <td className="p-2">
                            {r.status === "error" ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700" title={r.errors?.join(", ")}>Error</span>
                            ) : (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.action === "create" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                                {r.action === "create" ? "Nuevo" : "Actualizar"}
                              </span>
                            )}
                          </td>
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2 text-xs">{r.city || "-"}</td>
                          <td className="p-2 text-xs">{r.owner_email}</td>
                          <td className="p-2 text-xs font-mono">{r.slug}</td>
                          <td className="p-2">
                            {!restImportApplying && <button onClick={() => setRestImportPreview(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(!restImportPreview || restImportPreview.length === 0) && (
              <p className="text-center py-8 text-sm" style={{ color: '#6B6A65' }}>No hay registros para importar</p>
            )}
          </div>
          <div className="border-t px-6 py-4 flex items-center justify-between" style={{ borderColor: '#E6E4DD', background: 'white' }}>
            {restImportApplying ? (
              <div className="w-full"><div className="w-full h-2 rounded-full" style={{ background: '#E6E4DD' }}>
                <div className="h-2 rounded-full animate-pulse" style={{ background: '#C05C46', width: '100%' }} /></div>
                <p className="text-xs text-center mt-1" style={{ color: '#6B6A65' }}>Importando...</p></div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm" style={{ color: '#6B6A65' }}>
                  {restImportPreview ? `${restImportPreview.filter(r => r.status === "ready").length} restaurantes listos` : ""}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowRestImportDialog(false)}>Cancelar</Button>
                  <Button style={{ background: '#C05C46' }} disabled={!restImportPreview || restImportPreview.filter(r => r.status === "ready").length === 0}
                    onClick={async () => {
                      setRestImportApplying(true);
                      try {
                        const ready = restImportPreview.filter(r => r.status === "ready");
                        const { data } = await api.admin.restaurantImportApply({ items: ready });
                        toast.success(data.message);
                        setShowRestImportDialog(false); setRestImportPreview(null); loadData();
                      } catch (e) { toast.error("Error al importar"); }
                      setRestImportApplying(false);
                    }}>
                    Importar {restImportPreview ? restImportPreview.filter(r => r.status === "ready").length : 0} restaurantes
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Content Import Dialog */}
      <Dialog open={showMenuImportDialog} onOpenChange={(v) => { if (!menuImportApplying) setShowMenuImportDialog(v); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="font-heading">Importar Contenido al Menú: {stdMenus.find(m => m.id === selectedMenuId)?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6">
            {menuImportErrors.length > 0 && (
              <div className="mb-3 p-3 rounded-lg text-xs" style={{ background: '#FFF3E6', border: '1px solid #F0E0C0' }}>
                <strong style={{ color: '#D4772C' }}>{menuImportErrors.length} errores:</strong>
                <div className="mt-1 space-y-0.5">
                  {menuImportErrors.map((e, i) => (
                    <div key={i} style={{ color: '#8B7D6B' }}>Fila {e.row}: {e.error}</div>
                  ))}
                </div>
              </div>
            )}
            {menuImportPreview && menuImportPreview.length > 0 && (
              <div className="border rounded-lg overflow-x-auto" style={{ borderColor: '#E6E4DD' }}>
                <table className="w-full text-sm">
                  <thead><tr className="border-b" style={{ borderColor: '#E6E4DD', background: '#FDFBF7' }}>
                    <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Código</th>
                    <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Producto</th>
                    <th className="text-right p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Precio Base</th>
                    <th className="text-right p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Precio Menú</th>
                    <th className="text-left p-2 text-xs font-semibold" style={{ color: '#6B6A65' }}>Sección</th>
                    <th className="p-2"></th>
                  </tr></thead>
                  <tbody>
                    {menuImportPreview.map((r, idx) => (
                      <tr key={idx} className="border-b last:border-0" style={{ borderColor: '#E6E4DD' }}>
                        <td className="p-2 text-xs font-mono">{fmtCode(r.unique_code)}</td>
                        <td className="p-2 text-sm font-medium">{r.product_name}</td>
                        <td className="p-2 text-xs text-right" style={{ color: '#6B6A65' }}>{fmtPrice(r.base_price)}</td>
                        <td className="p-2 text-sm text-right font-bold" style={{ color: '#C05C46' }}>{r.menu_price != null ? fmtPrice(r.menu_price) : "Base"}</td>
                        <td className="p-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.section_exists ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                            {r.section}{!r.section_exists && " (nueva)"}
                          </span>
                        </td>
                        <td className="p-2">
                          {!menuImportApplying && <button onClick={() => setMenuImportPreview(prev => prev.filter((_, i) => i !== idx))}
                            className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(!menuImportPreview || menuImportPreview.length === 0) && menuImportErrors.length === 0 && (
              <p className="text-center py-8 text-sm" style={{ color: '#6B6A65' }}>No hay registros válidos para importar</p>
            )}
          </div>
          <div className="border-t px-6 py-4 flex items-center justify-between" style={{ borderColor: '#E6E4DD', background: 'white' }}>
            {menuImportApplying ? (
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1">
                  <div className="w-full h-2 rounded-full" style={{ background: '#E6E4DD' }}>
                    <div className="h-2 rounded-full animate-pulse" style={{ background: '#C05C46', width: '100%' }} />
                  </div>
                  <p className="text-xs text-center mt-1" style={{ color: '#6B6A65' }}>Importando...</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-sm" style={{ color: '#6B6A65' }}>
                  {menuImportPreview ? `${menuImportPreview.length} productos listos` : ""}
                  {menuImportErrors.length > 0 ? ` · ${menuImportErrors.length} errores` : ""}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowMenuImportDialog(false)}>Cancelar</Button>
                  <Button style={{ background: '#C05C46' }} disabled={!menuImportPreview || menuImportPreview.length === 0}
                    onClick={async () => {
                      setMenuImportApplying(true);
                      try {
                        const { data } = await api.admin.menuImportApply(selectedMenuId, {
                          items: menuImportPreview.map(r => ({
                            product_id: r.product_id, product_name: r.product_name,
                            base_price: r.base_price, menu_price: r.menu_price, section: r.section
                          }))
                        });
                        toast.success(`${data.imported} importados, ${data.sections_created} secciones creadas`);
                        setShowMenuImportDialog(false); setMenuImportPreview(null); setMenuImportErrors([]);
                        loadMenuContent(selectedMenuId); loadData();
                      } catch (e) { toast.error("Error al importar"); }
                      setMenuImportApplying(false);
                    }}>
                    Importar {menuImportPreview ? menuImportPreview.length : 0} productos
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
